import { NavMenuContentType, formatHealthTool } from '@hhgtech/hhg-components'
import {
  FooterInfoMarryBaby,
  HealthToolApi,
} from '@hhgtech/hhg-components/types'
import { PATHS } from 'api/paths'
import {
  callApi,
  checkMobile,
  cleanUnicodeUrl,
  getApiPath,
  verifyNotFoundToRedirect,
} from 'api/utils'
import {
  BabyGrowthPageProps,
  BabyGrowthPageType,
  getServerSideProps as getBabyGrowthServerSideProps,
} from 'components/pages/healthTools/babyGrowth/index'
import { BabyGrowthResultPageProps } from 'components/pages/healthTools/babyGrowth/result'
import {
  getServerSideProps as getBabyPoopPageServerSideProps,
  BabyPoopPageProps,
} from 'components/pages/healthTools/babyPoop/index'
import type { BabyVaccinationPageAllProps } from 'components/pages/healthTools/babyVaccine'
import { getServerSideProps as getBabyVaccineServerSideProps } from 'components/pages/healthTools/babyVaccine/getServerSideProps'
import {
  getServerSideProps as getBMIServerSideProps,
  Props as BMIPageProps,
} from 'components/pages/healthTools/bmiPage'
import {
  getServerSideProps as getBMRServerSideProps,
  Props as BMRPageProps,
} from 'components/pages/healthTools/bmrPage'
import {
  getServerSideProps as getDueDateServerSideProps,
  Props as DueDatePageProps,
} from 'components/pages/healthTools/due-date'
import {
  getServerSideProps as getOvulationServerSideProps,
  Props as OvulationPageProps,
} from 'components/pages/healthTools/ovulation'
import {
  getServerSideProps as getPWGServerSideProps,
  Props as PWGPageProps,
} from 'components/pages/healthTools/pwg'
import { HEALTH_TOOL_TYPE } from 'interfaces/constants'
import { Category, HealthTool } from 'interfaces/types'
import { GetServerSideProps, GetServerSidePropsResult } from 'next'
import dynamic from 'next/dynamic'

const DueDatePageComponent = dynamic<DueDatePageProps>(() =>
  import('components/pages/healthTools/due-date').then(
    (mod) => mod.DueDatePageComponent,
  ),
)

const BMIPageComponent = dynamic<BMIPageProps>(() =>
  import('components/pages/healthTools/bmiPage').then((mod) => mod.BMIToolPage),
)

const BMRPageComponent = dynamic<BMRPageProps>(() =>
  import('components/pages/healthTools/bmrPage').then((mod) => mod.BMRToolPage),
)

const OvulationPageComponent = dynamic<OvulationPageProps>(() =>
  import('components/pages/healthTools/ovulation').then(
    (mod) => mod.OvulationPageComponent,
  ),
)
const BabyGrowthPageComponent = dynamic<
  (BabyGrowthPageProps | BabyGrowthResultPageProps) & {
    type: BabyGrowthPageType
  }
>(() =>
  import('components/pages/healthTools/babyGrowth/index').then(
    (mod) => mod.BabyGrowthPage,
  ),
)

const PWGPageComponent = dynamic<PWGPageProps>(() =>
  import('components/pages/healthTools/pwg').then((mod) => mod.PWGToolPage),
)

export const BabyPoopPageComponent = dynamic<BabyPoopPageProps>(() =>
  import('components/pages/healthTools/babyPoop').then(
    (mod) => mod.BabyPoopPage,
  ),
)
export const BabyVaccinationPageComponent = dynamic<BabyVaccinationPageAllProps>(
  () =>
    import('components/pages/healthTools/babyVaccine').then(
      (mod) => mod.BabyVaccinationPage,
    ),
)
export type BaseProps = {
  isMobileSsr?: boolean
  pageType?: string
  resolvedUrl?: string
  healthToolTitle?: string
  toolName?: string
  siteName?: string
  topCategories?: Category[]
  currentCategory?: string
  footerMenu: FooterInfoMarryBaby
  dataNavMenuContent?: NavMenuContentType | null
}
export type Props =
  | DueDatePageProps
  | BMIPageProps
  | BMRPageProps
  | OvulationPageProps
  | BabyGrowthPageProps
  | PWGPageProps
  | BabyPoopPageProps
  | BabyVaccinationPageAllProps

const HealthToolsPage = (props: Props) => {
  const { pageType } = props
  if (pageType == HEALTH_TOOL_TYPE.DUE_DATE) {
    return <DueDatePageComponent {...(props as DueDatePageProps)} />
  } else if (pageType === HEALTH_TOOL_TYPE.BMI) {
    return <BMIPageComponent {...(props as BMIPageProps)} />
  } else if (pageType === HEALTH_TOOL_TYPE.BMR) {
    return <BMRPageComponent {...(props as BMRPageProps)} />
  } else if (pageType === HEALTH_TOOL_TYPE.OVULATION) {
    return <OvulationPageComponent {...(props as OvulationPageProps)} />
  } else if (pageType === HEALTH_TOOL_TYPE.BABY_GROWTH) {
    return <BabyGrowthPageComponent {...(props as any)} />
  } else if (pageType === HEALTH_TOOL_TYPE.PWG) {
    return <PWGPageComponent {...(props as PWGPageProps)} />
  } else if (pageType === HEALTH_TOOL_TYPE.BABY_POOP) {
    return <BabyPoopPageComponent {...(props as any)} />
  } else if (pageType === HEALTH_TOOL_TYPE.BABY_VACCINE) {
    return <BabyVaccinationPageComponent {...(props as any)} />
  }
  return null
}

// api1

export const getServerSideProps: GetServerSideProps<Props | BaseProps> = async (
  ctx,
) => {
  const { query, req, locale = 'vi-VN' } = ctx
  const userAgent = `${req.headers['user-agent']}`
  const isMobileSsr = checkMobile(userAgent)
  const { slug } = query

  let stringSlug = Array.isArray(slug) ? slug.join('/') : slug
  stringSlug = cleanUnicodeUrl(stringSlug || '').replace(/health-tools\//gi, '')
  const healthToolsData = await callApi(
    getApiPath(PATHS.GET_ALL_HEALTH_TOOLS),
    'GET',
  )
  const allHealthTools: HealthTool[] =
    healthToolsData?.data?.map((tool: HealthToolApi) =>
      formatHealthTool(tool),
    ) || []

  const currentHealthTool = allHealthTools.find((tool: HealthTool) =>
    stringSlug?.startsWith(
      cleanUnicodeUrl(tool.link)
        .replace(/health-tools\//gi, '')
        .split('/')
        .filter(Boolean)
        .join('/'),
    ),
  )

  let returnRes: GetServerSidePropsResult<Props | BaseProps> = {
    notFound: true,
  }

  if (currentHealthTool?.template) {
    switch (currentHealthTool.template) {
      case HEALTH_TOOL_TYPE.DUE_DATE:
        returnRes = await getDueDateServerSideProps(ctx)
        break
      case HEALTH_TOOL_TYPE.BMI:
        returnRes = await getBMIServerSideProps(ctx)
        break
      case HEALTH_TOOL_TYPE.BMR:
        returnRes = await getBMRServerSideProps(ctx)
        break
      case HEALTH_TOOL_TYPE.OVULATION:
        returnRes = await getOvulationServerSideProps(ctx)
        break
      case HEALTH_TOOL_TYPE.BABY_GROWTH:
        returnRes = await getBabyGrowthServerSideProps(ctx)
        break
      case HEALTH_TOOL_TYPE.PWG:
        returnRes = await getPWGServerSideProps(ctx)
        break
      case HEALTH_TOOL_TYPE.BABY_POOP: {
        returnRes = await getBabyPoopPageServerSideProps(ctx)
        break
      }
      case HEALTH_TOOL_TYPE.BABY_VACCINE: {
        returnRes = await getBabyVaccineServerSideProps(ctx)
        break
      }
      default:
        break
    }
  } else {
    returnRes = verifyNotFoundToRedirect(
      slug,
      locale,
    ) as GetServerSidePropsResult<Props | (BaseProps & Props)>

    if ('notFound' in returnRes && returnRes.notFound) {
      returnRes = {
        redirect: {
          destination: '/health-tools/',
          statusCode: 301,
        },
      }
    }
  }

  if ('props' in returnRes) {
    returnRes.props.isMobileSsr = isMobileSsr
  }

  return returnRes
}

export default HealthToolsPage
